import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import type { ExpandableProps } from './Expandable.types';

import styles from './Expandable.module.scss';

export const Expandable = ({
  caption,
  children,
  id,
  onClick,
}: ExpandableProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isNativeImplementation, setIsNativeImplementation] = useState(true);
  const [maxHeight, setMaxHeight] = useState<string | undefined>('0');

  const accordion = useRef<HTMLDetailsElement | null>(null);
  const content = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    onClick?.(e, isExpanded);
    setIsExpanded(!isExpanded);
  };

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (e.propertyName !== 'max-height') {
      return;
    }

    if (isExpanded) {
      setMaxHeight(undefined);
    }
  }, [isExpanded]);

  useEffect(() => setIsNativeImplementation(false), []);

  useEffect(() => {
    const expandableComponent = accordion.current;

    const isContentExpanded = content.current && isExpanded;
    const isAccordionOpen = expandableComponent && isExpanded;
    const isAccordionClosed = expandableComponent && !isExpanded;

    if (isAccordionOpen) {
      expandableComponent.open = isExpanded;
    }

    if (isAccordionClosed) {
      const currentHeight = content.current?.scrollHeight ?? 0;
      setMaxHeight(`${currentHeight}px`);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setMaxHeight('0');
        });
      });

      timer.current = setTimeout(() => {
        expandableComponent.open = isExpanded;
      }, 400);
    }

    if (isContentExpanded) {
      setMaxHeight(`${content.current?.scrollHeight}px`);
    }

    return () => {
      clearTimeout(timer.current);
    };
  }, [isExpanded]);

  return (
    <div className={cx(styles.component, {
      [styles.expanded]: isExpanded,
      [styles.nativeComponent]: isNativeImplementation,
    })}>
      <details
        ref={accordion}
        className={styles.accordion}
        {...{ id }}
      >
        <summary
          onClick={(e) => handleClick(e)}
          tabIndex={0}
        >
          <div className={styles.caption}>
            <h3 className={styles.text}>{caption}</h3>
            <span className={styles.icon} />
          </div>
        </summary>
        <div
          className={styles.contentWrapper}
          onTransitionEnd={handleTransitionEnd}
          style={maxHeight !== undefined ? { maxHeight } : undefined}
        >
          <div
            ref={content}
            className={styles.content}
            tabIndex={-1}
          >
            {children}
          </div>
        </div>
      </details>
    </div>
  );
};
